import axios from 'axios';
import type { AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios';

export const API_BASE_URL = (() => {
  // 1. Strictly prefer the environment variable if present
  if (process.env.REACT_APP_API_BASE_URL) {
    let url = (process.env.REACT_APP_API_BASE_URL as string).trim();
    // Absolute URL with protocol
    if (/^https?:\/\//i.test(url)) {
      return url.replace(/\/$/, '');
    }
    // Relative path (e.g., "/api")
    if (url.startsWith('/')) {
      return url.replace(/\/$/, '');
    }
    // Host or host:port (e.g., "api.focusbees.com" or "api.focusbees.com:3001")
    // Default to current page protocol when available, else https
    const scheme =
      typeof window !== 'undefined' && window.location && window.location.protocol
        ? window.location.protocol
        : 'https:';
    return `${scheme}//${url}`.replace(/\/$/, '');
  }

  // 2. Check for localhost development environment & Local Network IPs
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    // Localhost
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:3001';
    }
    // Private Network IPs (192.168.x.x, 10.x.x.x, 172.16-31.x.x)
    // This allows mobile devices on the same network to connect to the backend port directly
    // since the frontend dev server (port 3000) might not proxy correctly.
    if (
      host.startsWith('192.168.') ||
      host.startsWith('10.') ||
      /^172\.(1[6-9]|2\d|3[0-1])\./.test(host)
    ) {
      return `http://${host}:3001`;
    }
  }

  // 3. Fallback for production/preview if env var is missing (should not happen if configured correctly)
  // We avoid falling back to localhost to prevent security/CORS issues in production.
  // Returning relative path '/api' is a safer default for same-domain proxies, 
  // or empty string if we want to fail hard (though that might break app startup).
  return '/api'; 
})();

export const api = axios;
try {
  if ((api as any)?.defaults) {
    (api as any).defaults.baseURL = API_BASE_URL;
    (api as any).defaults.withCredentials = true;
    (api as any).defaults.timeout = 30000;
    
    // Restore token from localStorage if present (fixes Safari/refresh issues)
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token && (api as any).defaults.headers.common) {
        (api as any).defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
    }
  }
} catch {}

try {
  if ((api as any)?.interceptors?.response) {
    api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const status = error?.response?.status;
        if (status === 401 && !(error.config as any)?.skipAuthRedirect) {
          try { 
            localStorage.removeItem('userId');
            localStorage.removeItem('token');
            // Optional: Redirect to login if not already there
            if (typeof window !== 'undefined' && !window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
                // We use a query param so the login page can show "Session expired"
                window.location.href = '/login?reason=expired';
            }
          } catch {}
        }
        const isNetwork = !error.response && (error.code === 'ERR_NETWORK' || /Network Error/i.test(String(error.message)));
        const cfg = error.config as (AxiosRequestConfig & { __retried?: boolean }) | undefined;
        // Only retry on localhost/dev environments, not production
        const isDev = typeof window !== 'undefined' && (
          window.location.hostname === 'localhost' || 
          window.location.hostname === '127.0.0.1' ||
          window.location.hostname.startsWith('192.168.') ||
          window.location.hostname.startsWith('10.') ||
          /^172\.(1[6-9]|2\d|3[0-1])\./.test(window.location.hostname)
        );
        
        if (isNetwork && cfg && !cfg.__retried && isDev && typeof (axios as any)?.create === 'function') {
          try {
        const current = ((api as any)?.defaults?.baseURL) || '';
        const u = new URL(current);
        const origScheme = u.protocol;
        const flippedScheme = origScheme === 'https:' ? 'http:' : 'https:';
        const host = u.hostname;
        const port = u.port || '3001';
        const candidates = [
          `${origScheme}//127.0.0.1:${port}`,
          `${flippedScheme}//${host}:${port}`,
          `${flippedScheme}//127.0.0.1:${port}`,
        ];
        for (const altBase of candidates) {
          try {
            const alt = (axios as any).create({ baseURL: altBase, withCredentials: true, timeout: 30000 });
            const newCfg: AxiosRequestConfig & { __retried?: boolean } = { ...cfg, baseURL: altBase, __retried: true };
            const resp = await alt.request(newCfg);
            try { localStorage.setItem('apiBase', altBase); } catch {}
            return resp as any;
          } catch {
            // try next candidate
          }
        }
          } catch (_) {
            // fall through to original error
          }
        }
        return Promise.reject(error);
      }
    );
  }
} catch {}
